// umlaut-check: skip — technische Bezeichner (verfuegbar) der Bestandskennzahlen.
import React from 'react';
import OeTip from './OeTip';
import BestandTooltip, {werteAusKennzahlen} from '../Stock/BestandTooltip';
import {availOf, veVorschau} from './availability';
import {fmt0} from './format';

// Vorschau der VE-Regel vor dem Anlegen der Lieferscheine (Auftrag #315, Mail
// Sebastian Fischinger 28.09.2026, Punkt 5): Welche Menge kommt wegen der
// Verkaufseinheit (VE) auf den Lieferschein, und wie viel geht in die
// FM-Datei? Gezeigt werden nur Positionen, bei denen die Rundung etwas ändert.
// Ohne solche Positionen verschwindet der ganze Baustein.
//
// Die Vorschau rechnet mit dem verfügbaren Bestand im Lieferlager der Position
// (einheitliche Bestandsregel). Verbindlich korrigiert der Server den
// Lieferschein nach dem Anlegen in weclapp und vor dem Festschreiben.

const KOPFTEXT = {
    ve: 'Verkaufseinheit (VE) des Artikels, meist 6 Dosen. Alle Vertriebswege außer PG1 (Endkunden) bestellen ganze VE.',
    offen: 'Noch offene Menge der Position: bestellt abzüglich bereits gelieferter Menge.',
    verfuegbar: 'Verfügbare Menge im Lieferlager der Position nach der einheitlichen Bestandsregel (Lagerbestand minus von anderen Aufträgen und Lieferscheinen reserviert).',
    liefern: 'Diese Menge kommt auf den Lieferschein: die verfügbare Menge, abgerundet auf ganze Verkaufseinheiten. Bei 0 fällt die Position vom Lieferschein.',
    fm: 'Diese Menge fehlt auf dem Lieferschein und steht in der Fehlmengen-Datei (FM-Datei) am Auftrag: offen minus Lieferschein-Menge.',
};

/**
 * @param {object} props
 * @param {object} props.ctx   - Kontext des Ablaufs ({order, stock, pending, ...})
 * @param {Array}  props.rows  - Positionen, für die der Lieferschein angelegt wird
 */
export default function VeVorschau({ctx, rows}) {
    const {stock, order, pending} = ctx;
    const liste = veVorschau(rows, stock, order, pending);

    if (!liste.length) {
        return null;
    }

    const summeFm = liste.reduce((n, p) => n + p.fm, 0);

    return (
        <div className="statusmatrix ve-vorschau">
            <div className="sm-h">
                📦 VE-Regel: {liste.length} Position{liste.length === 1 ? '' : 'en'} auf ganze Verkaufseinheiten abgerundet
                <OeTip
                    tip={'Alle Vertriebswege außer PG1 (Endkunden, Einzeldosen) bestellen ganze Verkaufseinheiten (VE).'}
                    detail={'Ist weniger verfügbar als offen, kommt nur die nächste ganze VE nach unten auf den Lieferschein; '
                        + 'der Rest steht in der Fehlmengen-Datei. '
                        + 'Die Vorschau gilt für den Lieferschein, der gleich angelegt wird. '
                        + 'weclapp legt beim Anlegen teils Einzeldosen auf den Lieferschein — der Server korrigiert die Menge '
                        + 'danach, noch vor dem Festschreiben. Einstellbar unter Systemeinstellungen → Auftrags-Editor.'}
                >
                    <span className="poinfo muted" style={{display: 'inline', marginLeft: 6}}>?</span>
                </OeTip>
            </div>

            <table className="smtx">
                <thead>
                    <tr>
                        <th>Position</th>
                        <th><OeTip tip={KOPFTEXT.ve}><span>VE</span></OeTip></th>
                        <th><OeTip tip={KOPFTEXT.offen}><span>offen</span></OeTip></th>
                        <th><OeTip tip={KOPFTEXT.verfuegbar}><span>verfügbar</span></OeTip></th>
                        <th><OeTip tip={KOPFTEXT.liefern}><span>auf den Lieferschein</span></OeTip></th>
                        <th><OeTip tip={KOPFTEXT.fm}><span>in die FM-Datei</span></OeTip></th>
                    </tr>
                </thead>
                <tbody>
                    {liste.map((p) => (
                        <tr key={p.key}>
                            <td>{p.articleNumber} <span className="muted">{p.title.slice(0, 34)}</span></td>
                            <td>{fmt0(p.ve)}</td>
                            <td>{fmt0(p.offen)}</td>
                            <td>
                                <BestandTooltip
                                    werte={werteAusKennzahlen(availOf(p.row, stock, order))}
                                    kopf="Verfügbar im Lieferlager dieser Position."
                                >
                                    <span>{fmt0(p.verfuegbar)}</span>
                                </BestandTooltip>
                            </td>
                            <td>
                                <OeTip tip={p.liefern > 0
                                    ? `${fmt0(p.liefern)} Stück (${fmt0(p.liefern / p.ve)} ganze VE) kommen auf den Lieferschein.`
                                    : 'Nicht einmal eine ganze VE ist verfügbar: die Position fällt vom Lieferschein.'}>
                                    <b className={p.liefern > 0 ? '' : 'muted'}>{fmt0(p.liefern)}</b>
                                </OeTip>
                            </td>
                            <td>
                                <OeTip tip={`${fmt0(p.fm)} Stück gehen in die Fehlmengen-Datei am Auftrag.`}>
                                    <span style={{color: 'var(--warn)'}}>{fmt0(p.fm)}</span>
                                </OeTip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="lg-desc" style={{marginTop: 6}}>
                Zusammen gehen <b>{fmt0(summeFm)} Stück</b> in die FM-Datei.
            </div>
        </div>
    );
}
